import { Button, Grid, Group, Radio, Select, Stack, Textarea, TextInput } from '@mantine/core'
import type { UseFormReturnType } from '@mantine/form'
import { useHotkeys } from '@mantine/hooks'
import type { AssetGroupSchema, AssetTarget } from '@opsopti/shared'
import {
  assetGroupSchema,
  castAssetGroupToForm,
  isFieldsListValid,
  useAssetGroupCreateMutation,
  useAssetGroupForm,
  useAssetGroupQuery,
  useAssetGroupUpdateMutation,
  useAssetTargetsQuery,
  useGlobalState,
} from '@opsopti/shared'
import type { CreateAssetGroupResponse, UpdateAssetGroupResponse } from '@opsopti/shared/src/services/api/api.types'
import { useAreFormQueriesFetched } from '@opsopti/shared/src/utils/tanstackHelpers'
import { observer } from 'mobx-react-lite'
import { createContext, Fragment, useContext, useEffect, useMemo } from 'react'
import type { z } from 'zod'

import { RadioCard } from '@/components/RadioCard'
import type { ScreenProps } from '@/components/Screen'
import { Screen } from '@/components/Screen'
import { SectionedForm, type SectionedFormProps } from '@/components/SectionedForm'
import type { RouteParams } from '@/configs/routes'
import {
  getFormScreenTitle,
  getFormSubmitLabel,
  showErrorNotification,
  showSuccessNotification,
} from '@/utils/componentCommons'
import { goBack } from '@/utils/navigation'

import { TableScreenDescription, tableScreenIcon } from './TableScreen'

type AssetSentryGroupFormScreenProps = {
  type?: 'screen' | 'drawer'
  params?: RouteParams['assetSentryGroupNew'] | RouteParams['assetSentryGroupEdit']
  goBackFn?: () => void
  onAfterSubmitFn?: (group: CreateAssetGroupResponse | UpdateAssetGroupResponse) => void
}

type FormSection = {
  id: string | null
  type: 'screen' | 'drawer'
  form: UseFormReturnType<z.infer<AssetGroupSchema>>
  targets: AssetTarget[]
  isMutating: boolean
  submitFn: () => Promise<void>
  goBackFn: () => void
}

const AssetGroupFormDataContext = createContext<FormSection>({} as FormSection)
const useAssetGroupFormData = () => useContext(AssetGroupFormDataContext)

export const AssetSentryGroupFormScreen = observer(function AssetSentryGroupFormScreen(
  props: AssetSentryGroupFormScreenProps,
) {
  const { uiStore } = useGlobalState()

  const id = props.params?.id ?? null
  const isNew = useMemo(() => !id, [id])
  const type = props.type ?? 'screen'

  const { form } = useAssetGroupForm()

  const groupQuery = useAssetGroupQuery({ params: { id } })
  const targetsQuery = useAssetTargetsQuery({ params: { depth: '0' } })
  const groupCreateMutation = useAssetGroupCreateMutation({
    onErrorNotification: showErrorNotification,
    onSuccessNotification: showSuccessNotification,
  })
  const groupUpdateMutation = useAssetGroupUpdateMutation({
    onErrorNotification: showErrorNotification,
    onSuccessNotification: showSuccessNotification,
  })

  const areQueriesLoaded = useAreFormQueriesFetched([!isNew ? groupQuery.isFetched : null, targetsQuery.isFetched], 200)

  useEffect(() => {
    if (areQueriesLoaded) {
      form.initialize(castAssetGroupToForm(groupQuery.data))
      uiStore.hideLoadingOverlay()
    } else {
      uiStore.showLoadingOverlay()
    }
  }, [areQueriesLoaded])

  useEffect(() => uiStore.hideLoadingOverlay, [])

  useHotkeys([['mod+S', submitFn]], [])

  const goBackFn = props.goBackFn ?? goBack

  async function submitFn() {
    const validations = form.validate()

    if (validations.hasErrors) {
      showErrorNotification({ title: 'Form Errors', message: 'Please fill out all required fields before submitting.' })
      return
    }

    let response: CreateAssetGroupResponse | UpdateAssetGroupResponse | undefined

    if (isNew) {
      response = await groupCreateMutation.mutateAsync(form.getValues())
    } else {
      response = await groupUpdateMutation.mutateAsync({ ...form.getValues(), id: id! })
    }

    props.onAfterSubmitFn?.(response)

    goBackFn()
  }

  const sections = useMemo(
    () =>
      [
        {
          id: 'general',
          title: 'General Details',
          subtitle: 'Basic details about the group.',
          collapsible: false,
          valid: isFieldsListValid(form, ['name']),
          ContentComponent: GeneralFields,
        },
        {
          id: 'assetSettings',
          title: 'Settings for Assets',
          subtitle: 'Configuration for the Assets in this group.',
          collapsible: false,
          ContentComponent: AssetSettings,
        },
        {
          id: 'additional',
          title: 'Additional Information',
          subtitle: 'Visibility settings, notes, and other information.',
          collapsible: false,
          valid: isFieldsListValid(form, ['visibilityStatus']),
          ContentComponent: AdditionalFields,
        },
        {
          id: 'actions',
          collapsible: false,
          ContentComponent: FormActions,
        },
      ] satisfies SectionedFormProps['sections'],
    [form],
  )

  const isMutating = groupCreateMutation.isPending || groupUpdateMutation.isPending
  const FormWrapper = type === 'screen' ? Screen : Fragment
  const FormWrapperProps: ScreenProps =
    type === 'screen'
      ? {
          maw: 1440,
          title: getFormScreenTitle(id, 'Group', groupQuery.data?.name),
          icon: tableScreenIcon['groups'],
          description: <TableScreenDescription entity="groups" />,
        }
      : ({} as ScreenProps)

  return (
    <FormWrapper {...FormWrapperProps}>
      <AssetGroupFormDataContext.Provider
        value={{ id, form, submitFn, goBackFn, isMutating, targets: targetsQuery.data?.docs ?? [], type }}>
        <SectionedForm sections={sections} sectionsWithBorder={type === 'screen'} />
      </AssetGroupFormDataContext.Provider>
    </FormWrapper>
  )
})

function GeneralFields() {
  const props = useAssetGroupFormData()

  return (
    <Grid gutter={props.type === 'screen' ? 'xxl' : 'xl'}>
      <Grid.Col span={12}>
        <TextInput
          withAsterisk
          label="Name"
          description="The label for the group. Must be unique across all groups."
          {...props.form.getInputProps('name')}
        />
      </Grid.Col>
    </Grid>
  )
}

function AssetSettings() {
  const props = useAssetGroupFormData()

  return (
    <Stack>
      <Select
        searchable
        label="Base Target"
        placeholder="Select a base target..."
        description="When an asset in this group is being assigned, the base target will be pre-selected."
        data={props.targets.map(t => ({ value: t.id, label: t.name }))}
        {...props.form.getInputProps('defaultTarget')}
      />
    </Stack>
  )
}

function AdditionalFields() {
  const props = useAssetGroupFormData()

  const RadioWrapper = props.type === 'screen' ? Group : Stack
  const RadioComponent = props.type === 'screen' ? RadioCard : Radio

  return (
    <Grid gutter={props.type === 'screen' ? 'xxl' : 'xl'}>
      <Grid.Col span={12}>
        <Radio.Group label="Visibility" withAsterisk {...props.form.getInputProps('visibilityStatus')}>
          <RadioWrapper align="stretch">
            <RadioComponent
              value={assetGroupSchema.shape.visibilityStatus.enum.active}
              label="Active"
              description="Available for asset categorization."
            />

            <RadioComponent
              value={assetGroupSchema.shape.visibilityStatus.enum.archived}
              label="Archived"
              description="No longer in use."
            />
          </RadioWrapper>
        </Radio.Group>
      </Grid.Col>

      <Grid.Col span={12}>
        <Textarea
          label="Notes For Supervisors"
          autosize
          minRows={4}
          description="Notes that can be used for internal purposes. Only visible to Supervisors."
          {...props.form.getInputProps('internalNotes')}
        />
      </Grid.Col>
    </Grid>
  )
}
function FormActions() {
  const props = useAssetGroupFormData()

  return (
    <Stack gap="xxl">
      <Group gap="xl">
        <Button size="lg" radius="sm" onClick={props.submitFn} loading={props.isMutating}>
          {getFormSubmitLabel(props.id, 'Group')}
        </Button>

        {!props.isMutating && (
          <Button size="sm" radius="sm" variant="light" color="gray" onClick={props.goBackFn}>
            Cancel
          </Button>
        )}
      </Group>
    </Stack>
  )
}
